// Message models mirroring the Care API Messaging DTOs.
// Enums are numeric to match the backend (Care.Shared.Enums).

export enum MessageChannel {
  WhatsApp = 0,
  Sms = 1,
  Other = 2,
}

export enum MessageType {
  General = 0,
  AppointmentConfirmation = 1,
  Reminder = 2,
  FollowUp = 3,
  Campaign = 4,
}

export enum MessageLogStatus {
  Pending = 0,
  Sent = 1,
  Failed = 2,
  Skipped = 3,
}

export enum MessageCampaignStatus {
  Pending = 0,
  Processing = 1,
  Completed = 2,
  Failed = 3,
  Cancelled = 4,
}

export const MESSAGE_CHANNEL_LABELS: Record<MessageChannel, string> = {
  [MessageChannel.WhatsApp]: "WhatsApp",
  [MessageChannel.Sms]: "SMS",
  [MessageChannel.Other]: "Other",
};

// Channels the provider does not support yet. They stay visible in selects
// (discoverable for later) but are disabled, and compose state is normalized
// back to the default channel if it ever holds one of these.
export const UNAVAILABLE_CHANNELS: ReadonlySet<MessageChannel> = new Set([
  MessageChannel.WhatsApp,
]);

export const normalizeComposeChannel = (channel: number): number =>
  UNAVAILABLE_CHANNELS.has(channel as MessageChannel)
    ? MessageChannel.Sms
    : channel;

export const CHANNEL_OPTIONS: {
  value: MessageChannel;
  label: string;
  disabled: boolean;
}[] = (Object.keys(MESSAGE_CHANNEL_LABELS) as MessageChannel[]).map((key) => {
  const channel = Number(key) as MessageChannel;
  const unavailable = UNAVAILABLE_CHANNELS.has(channel);
  return {
    value: channel,
    label: unavailable
      ? `${MESSAGE_CHANNEL_LABELS[channel]} (unavailable)`
      : MESSAGE_CHANNEL_LABELS[channel],
    disabled: unavailable,
  };
});

export const CHANNEL_UNAVAILABLE_NOTE =
  "WhatsApp is not available yet — messages will be sent over SMS.";

export const MESSAGE_TYPE_LABELS: Record<MessageType, string> = {
  [MessageType.General]: "General",
  [MessageType.AppointmentConfirmation]: "Appointment Confirmation",
  [MessageType.Reminder]: "Reminder",
  [MessageType.FollowUp]: "Follow-up",
  [MessageType.Campaign]: "Campaign",
};

export const MESSAGE_LOG_STATUS_LABELS: Record<MessageLogStatus, string> = {
  [MessageLogStatus.Pending]: "Pending",
  [MessageLogStatus.Sent]: "Sent",
  [MessageLogStatus.Failed]: "Failed",
  [MessageLogStatus.Skipped]: "Skipped",
};

export const MESSAGE_LOG_STATUS_CLASS: Record<MessageLogStatus, string> = {
  [MessageLogStatus.Pending]: "pending",
  [MessageLogStatus.Sent]: "sent",
  [MessageLogStatus.Failed]: "failed",
  [MessageLogStatus.Skipped]: "skipped",
};

export const MESSAGE_CAMPAIGN_STATUS_LABELS: Record<
  MessageCampaignStatus,
  string
> = {
  [MessageCampaignStatus.Pending]: "Pending",
  [MessageCampaignStatus.Processing]: "Processing",
  [MessageCampaignStatus.Completed]: "Completed",
  [MessageCampaignStatus.Failed]: "Failed",
  [MessageCampaignStatus.Cancelled]: "Cancelled",
};

export const MESSAGE_CAMPAIGN_STATUS_CLASS: Record<
  MessageCampaignStatus,
  string
> = {
  [MessageCampaignStatus.Pending]: "pending",
  [MessageCampaignStatus.Processing]: "processing",
  [MessageCampaignStatus.Completed]: "completed",
  [MessageCampaignStatus.Failed]: "failed",
  [MessageCampaignStatus.Cancelled]: "cancelled",
};

export interface MessageTemplate {
  id: string;
  organizationId: string;
  name: string;
  channel: number;
  messageType: number;
  body: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy?: string | null;
  updatedBy?: string | null;
}

export interface MessageTemplateUpdateModel {
  name?: string;
  channel?: number;
  messageType?: number;
  body?: string;
  isActive?: boolean;
}

export interface MessageCampaign {
  id: string;
  organizationId: string;
  channel: number;
  messageType: number;
  templateId?: string | null;
  name?: string | null;
  body?: string | null;
  totalCount: number;
  successCount: number;
  failCount: number;
  status: number;
  createdBy?: string | null;
  createdAt: string;
  completedAt?: string | null;
}

export interface MessageLog {
  id: string;
  organizationId: string;
  clientId: string;
  campaignId?: string | null;
  templateId?: string | null;
  channel: number;
  messageType: number;
  body: string;
  status: number;
  phone?: string | null;
  relatedFollowUpId?: string | null;
  relatedAppointmentId?: string | null;
  errorMessage?: string | null;
  sentBy?: string | null;
  sentAt?: string | null;
  createdAt: string;
}

export interface SendSingleMessageRequest {
  organizationId: string;
  clientId: string;
  channel: number;
  messageType: number;
  templateId?: string | null;
  body?: string | null;
  relatedFollowUpId?: string | null;
  relatedAppointmentId?: string | null;
}

export interface SendMessageResult {
  logId: string;
  status: number;
  whatsAppUrl?: string | null;
  errorMessage?: string | null;
}

export interface SendCampaignRequest {
  organizationId: string;
  channel: number;
  messageType: number;
  templateId?: string | null;
  name?: string | null;
  body?: string | null;
  clientIds: string[];
}

export interface SendCampaignResult {
  campaignId: string;
  totalCount: number;
  successCount: number;
  failCount: number;
  status: number;
  items: SendMessageResult[];
}

// Compose form state shared by the workspace and the campaign create page.
export interface ComposeState {
  channel: number;
  messageType: number;
  templateId: string | null;
  body: string;
}

export const DEFAULT_COMPOSE: ComposeState = {
  channel: MessageChannel.Sms,
  messageType: MessageType.General,
  templateId: null,
  body: "",
};

export const MESSAGE_PLACEHOLDERS = [
  { token: "{ClientName}", label: "Client name" },
  { token: "{OrgName}", label: "Organization name" },
  { token: "{Phone}", label: "Client phone" },
  { token: "{orgPhone}", label: "Organization phone" },
] as const;

export interface RenderValues {
  ClientName?: string;
  OrgName?: string;
  Phone?: string;
  OrgPhone?: string;
}

// Replace {Placeholders} in a body with real values; missing tokens are kept
// as-is so the user still sees what will be substituted server-side.
export const renderBody = (body: string, values: RenderValues): string =>
  body
    .replaceAll("{ClientName}", values.ClientName ?? "{ClientName}")
    .replaceAll("{OrgName}", values.OrgName ?? "{OrgName}")
    .replaceAll("{Phone}", values.Phone ?? "{Phone}")
    .replaceAll("{orgPhone}", values.OrgPhone ?? "{orgPhone}");

// Bangla Unicode block (U+0980–U+09FF). Used to detect whether a message body
// contains any Bangla text.
const BANGLA_REGEX = /[\u0980-\u09FF]/;

// The regulatory footer appended when a message has no Bangla content, so every
// outgoing SMS still meets the Bangla requirement. {orgPhone} is a placeholder
// resolved by renderBody at send/preview time.
export const ORG_CONTACT_SUFFIX =
  "\n\nআরও তথ্যের জন্য এই নম্বরে যোগাযোগ করুন: {orgPhone}";

export const withBanglaContactLine = (body: string): string => {
  if (BANGLA_REGEX.test(body)) return body;
  const trimmed = body.replace(/\s+$/, "");
  return `${trimmed}${ORG_CONTACT_SUFFIX}`;
};